/**
 * RecognitionTest — live camera inference screen.
 *
 * Opens the rear camera, runs MobileNetV2 at ~3 fps, computes cosine
 * similarity against the provided reference embeddings.  When the
 * similarity stays above `threshold` for `holdMs` ms continuously the
 * result is shown in-place — the user can accept, adjust settings, or
 * go back to add more photos.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { generateEmbeddingsJSON } from "@ndp61/cms-shared";
import type { MarkerEmbeddingsFile } from "@ndp61/cms-shared";
import { openRearCamera, captureFrame } from "../lib/camera";
import { matchAgainstRefs } from "../lib/recognition";
import { FIELD_CAPTURE_DEFAULT_HOLD_MS } from "../constants";

export interface VerificationResult {
  peakSimilarity: number;
  sustainedHoldMs: number;
  threshold: number;
  holdMs: number;
  passed: boolean;
  testedAt: string;
}

interface Props {
  embeddings: MarkerEmbeddingsFile;
  threshold?: number;
  holdMs?: number;
  onComplete(result: VerificationResult): void;
  onGoBack(target: "settings" | "photos"): void;
}

const FRAME_INTERVAL_MS = 333; // ~3 fps

export function RecognitionTest({
  embeddings,
  threshold = 0.65,
  holdMs = FIELD_CAPTURE_DEFAULT_HOLD_MS,
  onComplete,
  onGoBack,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const loopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runningRef = useRef(true);

  // Similarity UI state
  const [similarity, setSimilarity] = useState(0);
  const [peakSimilarity, setPeakSimilarity] = useState(0);
  const [holdProgress, setHoldProgress] = useState(0); // 0-1
  const [phase, setPhase] = useState<"scanning" | "passed" | "error">(
    "scanning",
  );
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [result, setResult] = useState<VerificationResult | null>(null);

  // Mutable refs for hold-timer logic (avoid stale closure issues)
  const holdStartRef = useRef<number | null>(null);
  const peakRef = useRef(0);
  const resultCalledRef = useRef(false);

  const refVectors = embeddings.refs.map((r) => r.embedding);

  const stopCamera = useCallback(() => {
    runningRef.current = false;
    if (loopRef.current !== null) clearTimeout(loopRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  function resetState() {
    holdStartRef.current = null;
    peakRef.current = 0;
    resultCalledRef.current = false;
    setSimilarity(0);
    setPeakSimilarity(0);
    setHoldProgress(0);
    setPhase("scanning");
    setResult(null);
  }

  function startCamera() {
    resetState();
    setCameraError(null);
    runningRef.current = true;
    openRearCamera()
      .then((stream) => {
        if (!runningRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
        loopRef.current = setTimeout(() => void inferFrame(), 600);
      })
      .catch((err: unknown) => {
        setCameraError(
          err instanceof Error ? err.message : "Camera unavailable",
        );
        setPhase("error");
      });
  }

  async function inferFrame() {
    if (!runningRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      scheduleNext();
      return;
    }

    try {
      const blob = await captureFrame(video);
      if (!runningRef.current) return;

      const buffer = await generateEmbeddingsJSON(
        [{ label: "frame", blob }],
        () => {},
      );
      if (!runningRef.current) return;

      const parsed = JSON.parse(new TextDecoder().decode(buffer)) as MarkerEmbeddingsFile;
      const frameVec = parsed.refs[0]?.embedding;
      if (!frameVec) {
        scheduleNext();
        return;
      }

      const sim = matchAgainstRefs(frameVec, refVectors);
      setSimilarity(sim);

      if (sim > peakRef.current) {
        peakRef.current = sim;
        setPeakSimilarity(sim);
      }

      const now = Date.now();
      if (sim >= threshold) {
        if (holdStartRef.current === null) holdStartRef.current = now;
        const elapsed = now - holdStartRef.current;
        setHoldProgress(Math.min(elapsed / holdMs, 1));

        if (elapsed >= holdMs && !resultCalledRef.current) {
          resultCalledRef.current = true;
          const verResult: VerificationResult = {
            peakSimilarity: peakRef.current,
            sustainedHoldMs: elapsed,
            threshold,
            holdMs,
            passed: true,
            testedAt: new Date().toISOString(),
          };
          setPhase("passed");
          setResult(verResult);
          stopCamera();
          return;
        }
      } else {
        holdStartRef.current = null;
        setHoldProgress(0);
      }
    } catch {
      // non-fatal; log and keep looping
    }

    scheduleNext();
  }

  function scheduleNext() {
    if (!runningRef.current) return;
    loopRef.current = setTimeout(() => void inferFrame(), FRAME_INTERVAL_MS);
  }

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const simColor =
    similarity >= threshold
      ? "#2d7a2d"
      : similarity >= threshold * 0.8
        ? "#e07b00"
        : "#c8102e";

  return (
    <div style={s.container}>
      <h2 style={s.heading}>Step 4 — Live Recognition Test</h2>
      <p style={s.body}>
        Point the camera at the POI. Hold it steady until the hold bar fills.
      </p>

      {cameraError ? (
        <div style={s.errorBox}>
          <p style={s.errorText}>{cameraError}</p>
        </div>
      ) : (
        <div style={s.videoWrap}>
          {/* Live viewfinder */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={s.video}
            aria-label="Live camera feed"
          />

          {/* Similarity overlay */}
          <div style={s.overlay}>
            <span style={{ ...s.simBadge, background: simColor }}>
              {similarity.toFixed(2)}
            </span>
          </div>

          {/* Pass banner */}
          {phase === "passed" && (
            <div style={s.passBanner} aria-live="assertive">
              PASS
            </div>
          )}
        </div>
      )}

      {/* Hold progress bar */}
      <div style={s.holdTrack} role="progressbar" aria-valuenow={Math.round(holdProgress * 100)} aria-valuemin={0} aria-valuemax={100} aria-label="Hold progress">
        <div
          style={{
            ...s.holdFill,
            width: `${holdProgress * 100}%`,
            background: phase === "passed" ? "#2d7a2d" : "#c8102e",
          }}
        />
      </div>

      {/* Stats row */}
      <div style={s.statsRow}>
        <span style={s.stat}>
          Threshold: <strong>{threshold.toFixed(2)}</strong>
        </span>
        <span style={s.stat}>
          Peak: <strong style={{ color: simColor }}>{peakSimilarity.toFixed(2)}</strong>
        </span>
        <span style={s.stat}>
          Hold: <strong>{holdMs} ms</strong>
        </span>
      </div>

      {/* Result feedback */}
      {result && (
        <div style={s.resultBox}>
          <div style={s.resultHeader}>
            <span style={{
              ...s.badge,
              background: result.passed ? "#2d7a2d" : "#c0392b",
            }}>
              {result.passed ? "PASS" : "FAIL"}
            </span>
          </div>
          <div style={s.resultStats}>
            <span>Peak similarity: <strong>{result.peakSimilarity.toFixed(3)}</strong></span>
            <span>Threshold: <strong>{result.threshold.toFixed(2)}</strong></span>
            <span>Hold duration: <strong>{result.holdMs} ms</strong></span>
            <span>Sustained: <strong>{result.sustainedHoldMs} ms</strong></span>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {result ? (
        <div style={s.actionRow}>
          <button
            style={{ ...s.actionBtn, background: "#2d7a2d", color: "#fff" }}
            onClick={() => onComplete(result)}
          >
            Accept & Continue
          </button>
          <button
            style={{ ...s.actionBtn, background: "#0057b7", color: "#fff" }}
            onClick={() => onGoBack("settings")}
          >
            Adjust Settings
          </button>
          <button
            style={{ ...s.actionBtn, background: "#e07b00", color: "#fff" }}
            onClick={() => onGoBack("photos")}
          >
            Add More Photos
          </button>
          <button
            style={s.resetBtn}
            onClick={() => {
              stopCamera();
              startCamera();
            }}
          >
            Test Again
          </button>
        </div>
      ) : (
        <button
          style={s.resetBtn}
          onClick={() => {
            stopCamera();
            startCamera();
          }}
          aria-label="Reset test"
        >
          Test Again
        </button>
      )}
    </div>
  );
}

// -- Inline styles ------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  heading: {
    fontSize: 17,
    fontWeight: 700,
    color: "#111",
  },
  body: {
    fontSize: 14,
    color: "#444",
    lineHeight: 1.5,
  },
  videoWrap: {
    position: "relative",
    width: "100%",
    aspectRatio: "4/3",
    background: "#000",
    borderRadius: 10,
    overflow: "hidden",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  overlay: {
    position: "absolute",
    top: 10,
    right: 10,
  },
  simBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 20,
    color: "#fff",
    fontSize: 18,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.02em",
  },
  passBanner: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(45,122,45,0.75)",
    color: "#fff",
    fontSize: 48,
    fontWeight: 900,
    letterSpacing: "0.05em",
  },
  holdTrack: {
    width: "100%",
    height: 12,
    background: "#e0e0e0",
    borderRadius: 6,
    overflow: "hidden",
  },
  holdFill: {
    height: "100%",
    borderRadius: 6,
    transition: "width 0.15s linear",
  },
  statsRow: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
  },
  stat: {
    fontSize: 13,
    color: "#6b6b6b",
  },
  resultBox: {
    background: "#f5f5f5",
    padding: "12px 16px",
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  resultHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  badge: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: 4,
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.05em",
  },
  resultStats: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 13,
    color: "#444",
  },
  actionRow: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  actionBtn: {
    minHeight: 48,
    padding: "0 24px",
    borderRadius: 8,
    border: "none",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  resetBtn: {
    minHeight: 44,
    padding: "0 20px",
    borderRadius: 8,
    border: "none",
    background: "#6b6b6b",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    alignSelf: "flex-start",
  },
  errorBox: {
    background: "#fdf0ef",
    padding: "12px",
    borderRadius: 8,
  },
  errorText: {
    fontSize: 13,
    color: "#c0392b",
    margin: 0,
  },
};
